import { isMac } from '@shared/utils/platform'

export const SIDEPANEL_BACKGROUND_BLUR_SETTING_KEY = 'sidepanel.background-blur.enabled'
export const DEFAULT_SIDEPANEL_BACKGROUND_BLUR_ENABLED = false

/**
 * How far the native blur feathers at the panel's top and bottom edges. Shorter
 * than the 96px inner-edge fade: that one has a whole desktop to spill into,
 * these two only have the gap above and below the panel.
 *
 * The blur ABI has no parameter for it, so this value is duplicated in
 * `packages/aries/xcode/Sources/SidepanelBlur.swift` (`verticalFadeEndPx`).
 * Change both together — here it sizes the rect the fade has to complete
 * within, there it sizes the ramp itself.
 */
export const SIDEPANEL_BLUR_VERTICAL_FADE_PX = 48

/**
 * How far the blur feathers inward from the panel's inner edge, passed to
 * native as `fadeEndPx`.
 */
export const SIDEPANEL_BLUR_INNER_FADE_PX = 96

/**
 * Transparent gutter between the band the renderer reports as the panel and
 * the visible card drawn inside it. The renderer spends it as padding
 * (`DEFAULT_SIDEPANEL_LEFT_PADDING`); the blur has to know it to land its fade
 * on the card's edge rather than the band's.
 */
export const SIDEPANEL_SURFACE_GUTTER_PX = 12

export interface SidepanelBlurRect {
  x: number
  y: number
  width: number
  height: number
}

/**
 * Grow the panel's band into the rect the native blur should occupy.
 *
 * Both axes use the same trick: let the fade *finish* outside the panel so the
 * glass meets full blur exactly at its own edge. Horizontally the inner ramp
 * completes at the card edge one gutter in, spilling the remainder onto the
 * desktop beside the panel. Vertically the top and bottom feathers complete
 * off the panel's rect for the same reason — deliberately unclamped, since a
 * full-height panel needs its ramps to finish off-screen for the seam to
 * disappear. The blur view is a subview of the content view, so the window
 * clips whatever runs past it.
 */
export function toNativeBlurRect(panel: SidepanelBlurRect, anchoredRight: boolean): SidepanelBlurRect {
  const innerOverhang = SIDEPANEL_BLUR_INNER_FADE_PX - SIDEPANEL_SURFACE_GUTTER_PX
  return {
    x: anchoredRight ? Math.max(0, panel.x - innerOverhang) : panel.x,
    y: panel.y - SIDEPANEL_BLUR_VERTICAL_FADE_PX,
    width: panel.width + innerOverhang,
    height: panel.height + SIDEPANEL_BLUR_VERTICAL_FADE_PX * 2,
  }
}

/** True only on macOS when the user has not disabled background blur. */
export const isSidepanelBackgroundBlurEnabled = (settingValue: boolean | undefined): boolean => {
  if (!isMac) {
    return false
  }

  return settingValue !== false
}
